import { css, Global } from '@emotion/react';
import { ConfigProvider } from 'antd';
import { ThemeConfig } from 'antd/es/config-provider/context';
import React from 'react';
import { decafThemeDark, decafThemeLight, getDarkStyles, getLightStyles } from './-styles';
import { ThemeContext } from './-theme-context';

export interface ThemeProviderProps {
  children: React.ReactNode;
  themeConfig?: ThemeConfig;
  theme?: 'dark' | 'light';
}

function mergeComponents(
  base?: ThemeConfig['components'],
  override?: ThemeConfig['components']
): ThemeConfig['components'] {
  if (!override) return base;
  if (!base) return override;

  const merged = { ...base } as Record<string, any>;

  for (const key of Object.keys(override)) {
    merged[key] = { ...merged[key], ...(override as Record<string, any>)[key] };
  }

  return merged as ThemeConfig['components'];
}

function mergeThemeConfig(base: ThemeConfig, override?: ThemeConfig): ThemeConfig {
  if (!override) {
    return base;
  }

  return {
    ...base,
    ...override,
    token: {
      ...base.token,
      ...override.token,
    },
    components: mergeComponents(base.components, override.components),
    cssVar: {
      ...base.cssVar,
      ...override.cssVar,
    },
  };
}

export function ThemeProvider(props: ThemeProviderProps) {
  const [value, setValue] = React.useState<'dark' | 'light'>(() => {
    const theme = props.theme || localStorage.getItem('decafAppTheme');
    if (theme === 'dark' || theme === 'light') {
      return theme;
    }
    return 'dark';
  });

  function setTheme() {
    const t = value === 'dark' ? 'light' : 'dark';
    setValue(t);
    localStorage.setItem('decafAppTheme', t);
  }

  let theme = value === 'light' ? decafThemeLight : decafThemeDark;
  theme = mergeThemeConfig(theme, props.themeConfig);
  theme = { ...theme, token: { ...theme.token, fontFamily: 'Inter, sans-serif' } };

  const globalStyles = value === 'light' ? getLightStyles(theme) : getDarkStyles(theme);

  return (
    <ThemeContext.Provider
      value={{
        theme: value,
        toggleTheme: setTheme,
      }}
    >
      <ConfigProvider theme={theme}>
        <Global
          styles={css`
            @import url(https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap);
          `}
        />
        <Global styles={globalStyles} />

        {props.children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
